import { createContext, useContext, useState, useEffect } from "react";
import apiClient from "../tools/axiosConfig";
import { URL } from "../tools/url";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessToken] = useState("");

  const [user, setUser] = useState(undefined); // undefined instead of null, so user isn't redirected on reload
  const [isAdmin, setIsAdmin] = useState(false);
  const [isContentProvider, setIsContentProvider] = useState(false);
  const [profileComplete, setProfileComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverHealthy, setServerHealthy] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [sessionExplanationSeen, setSessionExplanationSeen] = useState(false);

  const fetchRequiredUserInfo = async () => {
    const { data } = await apiClient.get('/getUserInfo/');
    const userInfo = data?.userInfo || {};
    setUser(userInfo);
    setIsAdmin(!!userInfo.is_admin);
    setIsContentProvider(!!userInfo.is_content_provider);
    setOnboardingCompleted(!!userInfo.has_completed_onboarding);
    setSessionExplanationSeen(!!userInfo.has_seen_session_explanation);
    setProfileComplete(!!userInfo.profile_complete);
  };

  useEffect(() => {
    // Proactively check server health so UI can react gracefully
    async function checkHealth() {
      try {
        const resp = await fetch(`${URL}/api/health/`, { method: 'GET' });
        setServerHealthy(resp.ok);
      } catch (_) {
        setServerHealthy(false);
      }
    }
    checkHealth();

    // Load the access token
    const at = localStorage.getItem("access_token");
    if (at) setAccessToken(at);
    else {
      setUser(null);
      setProfileComplete(null);
      setIsAdmin(false);
      setIsContentProvider(false);
    }

    // Respond to the `storage` event
    function storageEventHandler() {
      if (localStorage.getItem("access_token")) setAccessToken(localStorage.getItem("access_token"));
      else {
        setUser(null);
        setProfileComplete(null);
        setIsAdmin(false);
        setIsContentProvider(false);
      }
    }

    // Hook up the event handler
    window.addEventListener("storage", storageEventHandler, false);

    return () => {
      // Remove the handler when the component unmounts
      window.removeEventListener("storage", storageEventHandler);
    };
  }, []);

  useEffect(() => {
    const fetchUserInfo = async () => {
      if (accessToken) {
        setLoading(true);
        try {
          await fetchRequiredUserInfo();

        } catch (error) {
          console.error("Error fetching user info:", error);
          // Ensure state is consistent when backend is unreachable or token invalid
          setUser(null);
          setProfileComplete(null);
          setIsAdmin(false);
          setIsContentProvider(false);
          setOnboardingCompleted(false);
        }
        setLoading(false);
      }
    };
    fetchUserInfo();
  }, [accessToken]);

  const completeOnboarding = async () => {
    try {
      await apiClient.post('/onboarding/complete/');
      // Server guarantees success, just update local state
      setOnboardingCompleted(true);
      // User might need to fill info next, so check that
      await fetchRequiredUserInfo();
    } catch (e) {
      console.error('Failed to complete onboarding', e);
    }
  };

  // The lesson-screen tour is shown once per user, on entering their first program.
  // The local flag flips even if the save fails, so the program page doesn't send the
  // learner straight back into the tour they just finished.
  const markSessionExplanationSeen = async () => {
    setSessionExplanationSeen(true);
    try {
      await apiClient.post('/onboarding/session_explanation_seen/');
    } catch (e) {
      console.error('Failed to mark the lesson-screen tour as seen', e);
    }
  };

  const logout = () => {
    // Only clear authentication-related data instead of all localStorage
    localStorage.removeItem('access_token');
    // Clear any other auth-related items if they exist
    localStorage.removeItem('refresh_token'); // in case you add refresh tokens later
    localStorage.removeItem('user_id'); // if stored separately
    
    // Trigger storage event to update AuthContext
    window.dispatchEvent(new Event('storage'));
    
    // Optionally redirect to login page
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAdmin,
        isContentProvider,
        loading,
        profileComplete,
        fetchRequiredUserInfo,
        logout,
        serverHealthy,
        onboardingCompleted,
        completeOnboarding,
        sessionExplanationSeen,
        markSessionExplanationSeen,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
